import React, { useEffect, useRef, useState } from 'react';
import { UpdateOfferDialog } from './UpdateOfferDialog';
import { useDispatch, useSelector } from 'react-redux';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { apiConfigConsts, offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { Modal } from '@interstate/components/Modal';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { FSErrorMessageText, FSErrorMessageHeading, UpdateOfferWithFSHandleFooter } from './UpdateOfferDialog.style';
import { Button } from '@interstate/components/Button';
import { UpdateOfferDialogMode } from './UpdateOfferDialogMode';
import { recordSaveFlowStart, recordSaveFlowSuccess, recordSaveFlowTimeout, recordSaveFlowFailure } from './saveFlowTelemetry';

type UpdateOfferDialogWithFSHandleProps = { show: boolean; onHide: () => void };

const TIMEOUT_MS = 60 * 1000;

enum FsErrorSourceType {
    TIMEOUT = 'TIMEOUT',
    SOCKET_EVENT = 'SOCKET_EVENT'
}
const fsErrorHeadingText =
    'There was a problem with your deal data. Please fix the issue and try again. If it continues, contact your account rep.';

export const UpdateOfferWithFSEventsDialog: React.FC<UpdateOfferDialogWithFSHandleProps> = ({ show, onHide }) => {
    const [fsError, setFsError] = useState<FsErrorSourceType | null>(null);
    const [updateDialogDisplayMode, setUpdateDialogDisplayMode] = useState<UpdateOfferDialogMode | null>(null);
    const startTimeMsRef = useRef<number | null>(null);
    const fsEventTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const dealRefId = useSelector(offerSelectors.getDealRefId) || offerSessionUtils.getSessionValue(apiConfigConsts.FS_DEAL_REF_ID);
    const errorMessages = useSelector(baseDealSelectors.getAioFSFailures);
    const hasErrorMessages = Array.isArray(errorMessages) && errorMessages.length > 0;
    const dispatch = useDispatch();

    const userId = useSelector(bridgeUserSelectors.getBridgeId);

    const buildTelemetryData = () => ({
        userId
    });

    const clearTimer = () => {
        if (fsEventTimeoutRef.current) {
            clearTimeout(fsEventTimeoutRef.current);
            fsEventTimeoutRef.current = null;
        }
    };

    const updateFsErrorType = (errorType: FsErrorSourceType) => {
        setFsError(errorType);
        clearTimer();

        if (!startTimeMsRef.current) return;
        const waitTimeMs = Date.now() - startTimeMsRef.current;
        const telemetryData = { ...buildTelemetryData(), waitTimeMs };

        if (errorType === FsErrorSourceType.TIMEOUT) {
            recordSaveFlowTimeout(telemetryData);
        } else {
            recordSaveFlowFailure(errorMessages, telemetryData);
        }

        startTimeMsRef.current = null;
    };

    useEffect(() => {
        if (show) {
            // Clear the previous error messages when dialog is opened for saving the deal
            dispatch(baseDealActionCreators.clearAioDxgEventErrors());
            setFsError(null);
            clearTimer();
        }
    }, [show]);

    useEffect(() => {
        if (hasErrorMessages && !fsError) {
            updateFsErrorType(FsErrorSourceType.SOCKET_EVENT);
        }
    }, [hasErrorMessages]);

    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, []);

    useEffect(() => {
        if (!dealRefId && show) {
            startTimeMsRef.current = Date.now();
            recordSaveFlowStart(buildTelemetryData());
        }
    }, [dealRefId, show]);

    const hideErrorModal = () => {
        setFsError(null);
        onHide();
        dispatch(baseDealActionCreators.clearAioDxgEventErrors());
    };

    const handleDealRefIdReceived = () => {
        if (startTimeMsRef.current) {
            const waitTimeMs = Date.now() - startTimeMsRef.current;
            recordSaveFlowSuccess({ ...buildTelemetryData(), waitTimeMs });
            startTimeMsRef.current = null;
        }
    };

    useEffect(() => {
        if (dealRefId) {
            handleDealRefIdReceived();
        }
    }, [dealRefId]);

    const handleOfferSaveSuccess = () => {
        if (dealRefId) {
            onHide();
            clearTimer();
        } else if (!fsError) {
            fsEventTimeoutRef.current = setTimeout(() => updateFsErrorType(FsErrorSourceType.TIMEOUT), TIMEOUT_MS);
        }
    };

    const handleHideOfferDialog = () => {
        if (updateDialogDisplayMode === UpdateOfferDialogMode.ERROR) {
            onHide();
        }
    };

    const renderErrorModal = () => {
        const messageTitle =
            fsError === FsErrorSourceType.SOCKET_EVENT ? 'We couldn’t submit your deal to Dealertrack' : 'Session timed out';
        const messageHeading = fsError === FsErrorSourceType.SOCKET_EVENT ? fsErrorHeadingText : null;
        const message =
            fsError === FsErrorSourceType.SOCKET_EVENT ? (
                <ul>
                    {(errorMessages || []).map((error, index) => (
                        <li key={index}>{error.message}</li>
                    ))}
                </ul>
            ) : (
                `It’s taking longer than expected. Please try again.`
            );
        return (
            <Modal
                size="large"
                header="Deal Processing Issue"
                data-testid="fs-event-error-modal"
                show={!!fsError}
                onHide={hideErrorModal}
            >
                <Alert type="error" data-testid="fs-event-error-alert">
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        {messageTitle}
                    </Typography>
                    <FSErrorMessageHeading variant="body-sm">{messageHeading}</FSErrorMessageHeading>
                    <FSErrorMessageText variant="body-sm">{message}</FSErrorMessageText>
                </Alert>
                <UpdateOfferWithFSHandleFooter>
                    <Button data-testid="fs-event-modal-button" buttonStyle="primary" onClick={hideErrorModal}>
                        OK
                    </Button>
                </UpdateOfferWithFSHandleFooter>
            </Modal>
        );
    };

    return (
        <>
            <UpdateOfferDialog
                show={show && !fsError}
                onSuccess={handleOfferSaveSuccess}
                onHide={handleHideOfferDialog}
                onDialogModeChange={setUpdateDialogDisplayMode}
                waitForWS
            />
            {show && renderErrorModal()}
        </>
    );
};
